import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  fetchStudioStockPhotos,
  fetchStudioTemplates,
  fetchStudioUploads,
} from "../../../features/studio-controller/StudioServices";
import { FONTS, loadFonts } from "../fonts";
import {
  ADJUSTMENTS,
  FRAME_SHAPES,
  GRIDS,
  LINES,
  LOOKS,
  SHAPES,
  TEXT_COMBOS,
  TEXT_EFFECTS,
  TEXT_PRESETS,
  applyLook,
  applyTextEffect,
  buildFromSpec,
  iconLibrary,
  iconSvg,
  makeGrid,
  makeIcon,
  makeLine,
  makePlaceholder,
  makeQr,
  makeShape,
  makeText,
  lookPreviews,
  readLook,
  renderSpecPreview,
  textColor,
  FrameShape,
  GridKind,
  TextEffect,
  AlignWhere,
} from "../fabricKit";
import { Icon, IconName } from "../Icon";
import { MediaPicker } from "../MediaPicker";
import {
  ColorPicker,
  FrameGlyph,
  Glyph,
  GlyphKind,
  GridGlyph,
  NumberBox,
  PALETTE,
  Slider,
  Tabs,
} from "./Controls";
import type { AppDispatch } from "../../../store";
import { FabricImage } from "fabric";
import { MediaItem, StudioUploadsResponse } from "../../../types/studioTypes";
import { util } from "fabric";
import { fetchGalleryPosts } from "../../../features/gallery/GalleryServices";
import { CategoryTiles, ElementCategory } from "./ElementForms";
import { DesignElements } from "./DesignElements";
import {
  GalleryPost,
  GallerySearchResponse,
} from "../../../types/galleryTypes";

/* ================================================================ types */

interface BrandLogo {
  galleryId: string | number;
  url?: string;
}
interface Brand {
  colors?: string[];
  headingFont?: string | null;
  bodyFont?: string | null;
  logos?: BrandLogo[];
}
interface MediaRef {
  id: number | string;
  mediaUrl: string;
}
interface EditorApi {
  panel: string;
  design: { width: number; height: number } | null;
  canvas: any; // fabric.Canvas
  brand: Brand;
  kind: string | null;
  selected: any; // fabric object / active selection
  features: {
    stockPhotos?: boolean;
    removeBackground?: boolean;
    upscale?: boolean;
    magicLayers?: boolean;
    aiElements?: boolean;
  };
  busy: string | null;
  snapOn: boolean;
  setPanel: (p: string) => void;
  add: (obj: any, select?: boolean) => void;
  addPage: (json?: string | null) => Promise<void>;
  insertImage: (
    item: MediaRef,
    extra?: Record<string, unknown>,
  ) => Promise<FabricImage | null>;
  insertStockPhoto: (photo: {
    id: number | string;
    photographer: string;
  }) => Promise<any>;
  setBackgroundPhoto: (item: MediaRef) => Promise<void>;
  imageToBackground: (img: any) => void;
  setBackground: (fill: string | string[], angle?: number) => void;
  removeBackgroundPhoto: () => void;
  removeImageBackground: (img: any) => Promise<void>;
  saveBrand: (next: Brand) => Promise<Brand | null>;
  applyBrandFonts: () => void;
  makeCombo: (combo: any, W: number, H: number) => any;
  makeText: (p: any, W: number, H: number, text?: string) => any;
  changed: () => void;
  refresh: () => void;
  notify: (text: string, kind?: "good" | "bad" | "info") => void;
  order: (where: "front" | "back" | "forward" | "backward") => void;
  moveLayer: (obj: any, toIndex: number) => void;
  toggleLock: (o?: any) => void;
  toggleVisible: (o: any) => void;
  selectLayer: (o: any) => void;
  startCrop: (img?: any) => void;
  endCrop: () => void;
  setDrawing: (
    opts: { kind?: "pen" | "highlighter"; color: string; width: number } | null,
  ) => void;
  align: (where: AlignWhere) => void;
  /** h = space across, v = space down (fabricKit distributeObjects). */
  distribute: (axis: "h" | "v") => void;
  group: () => void;
  ungroup: () => void;
  setSnapOn: (v: boolean) => void;
  [key: string]: any;
}
interface Template {
  id: string | number;
  name: string;
  category: string;
  width: number;
  height: number;
  specJson: string;
  thumbnailUrl?: string;
}
interface StockPhoto {
  id: string | number;
  thumbUrl: string;
  alt?: string;
  width: number;
  height: number;
  photographer: string;
}
interface Look {
  preset: string;
  intensity: number;
  adj: Record<string, number>;
  tint: { color: string; amount: number } | null;
}
type QrKind = "link" | "whatsapp" | "text";
type DrawKind = "pen" | "marker" | "highlighter";

/* ================================================================ tailwind class tokens */

const ring =
  "focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[rgba(11,122,90,0.45)]";
const DISPLAY = "font-['Bricolage_Grotesque','IBM_Plex_Sans',sans-serif]";

type Tone = "plain" | "accent" | "soft";
type Size = "md" | "sm" | "xs" | "align";
const TONE: Record<Tone, string> = {
  plain: "border-st-line bg-white text-st-ink hover:enabled:border-[#9fb3ab]",
  accent:
    "border-st-green bg-st-green text-white hover:enabled:border-st-green-dark hover:enabled:bg-st-green-dark",
  soft: "border-transparent bg-st-green-soft text-st-green-dark",
};
const SIZE: Record<Size, string> = {
  md: "min-h-[40px] rounded-10 px-[14px] py-[9px] text-sm",
  sm: "min-h-[34px] rounded-[9px] px-[11px] py-[7px] text-[13px]",
  xs: "min-h-[28px] rounded-[9px] px-2 py-[5px] text-xs",
  align: "min-h-[34px] rounded-[9px] px-[6px] py-[7px] text-[13px]",
};
const btn = (tone: Tone = "plain", size: Size = "md", block = false) =>
  `inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap border font-semibold leading-none disabled:cursor-not-allowed disabled:opacity-50 ${ring} ${SIZE[size]} ${TONE[tone]}${block ? " w-full" : ""}`;

const iconBtnSm = `inline-grid h-[30px] w-[30px] flex-none cursor-pointer place-items-center rounded-lg border-0 bg-transparent text-st-muted hover:enabled:bg-st-paper hover:enabled:text-st-ink disabled:cursor-not-allowed disabled:opacity-[0.35] ${ring}`;
const link = `inline-flex cursor-pointer items-center gap-[6px] border-0 bg-transparent py-1 text-sm font-semibold text-st-green-dark ${ring}`;
const linkBlock = `mt-[10px] block cursor-pointer border-0 bg-transparent py-1 text-left text-sm font-semibold text-st-green-dark ${ring}`;

const PANEL = "px-[18px] pb-[28px] pt-[18px]";
const H3 =
  "mb-[10px] mt-[22px] text-[13px] font-bold uppercase tracking-[0.06em] text-st-muted";
const HINT = "mt-2 text-[13px] leading-normal text-st-muted";
const HINT_TOP = "mb-3 text-[13px] leading-normal text-st-muted";
const FINEPRINT = "mt-2 text-xs leading-[1.45] text-st-muted";
const ERROR = "mt-2 text-[13px] text-st-red";
const CALLOUT =
  "mb-3 rounded-10 bg-st-green-soft px-3 py-[10px] text-[13px] font-semibold text-st-green-dark";
const SKEL = "block animate-pulse rounded-10 bg-[#edf2ef]";
const SKEL_FLAT = "block h-full w-full animate-pulse bg-[#edf2ef]";
const INPUT = `w-full resize-y rounded-[9px] border border-st-line bg-white px-[11px] py-[9px] text-sm text-st-ink ${ring}`;
const SELECT = `min-h-[36px] cursor-pointer rounded-[9px] border border-st-line bg-white py-[6px] pl-[10px] pr-[30px] text-sm text-st-ink ${ring}`;
const CARD_SOFT = "grid gap-2 rounded-xl bg-st-paper p-3";
const ROW_END = "mt-[10px] flex justify-end gap-[6px]";
const CHECK = "my-[10px] flex cursor-pointer items-center gap-2 text-sm";
const CHECK_INPUT = "h-[17px] w-[17px] flex-none accent-st-green";
const FIELD = "mb-[10px] grid gap-[6px] text-[13px] font-semibold";
const FIELD_ROW =
  "my-[10px] flex items-center justify-between gap-[10px] text-sm";
const SHAPE_GRID = "grid grid-cols-5 gap-[6px]";
const SHAPE_BTN = `grid aspect-square cursor-pointer place-items-center rounded-10 border border-st-line bg-st-paper text-[#3c5a50] hover:border-st-green hover:text-st-green [&_svg]:h-[34px] [&_svg]:w-[34px] ${ring}`;
const MEDIA_GRID_3 = "mt-[14px] grid grid-cols-3 gap-2";
const MEDIA_CONTAIN =
  "group relative aspect-square cursor-pointer overflow-hidden rounded-10 border-0 p-0 hover:ring-2 hover:ring-st-green [background:repeating-conic-gradient(#eef2f0_0_25%,#fff_0_50%)_0_0/14px_14px]";
const MEDIA_IMG =
  "block h-full w-full object-contain p-[6px] transition-transform duration-200 group-hover:scale-[1.04]";
const CP_ROW = "mb-1 flex flex-wrap gap-[6px]";
const cpSw = (on: boolean, lg = false) =>
  `${lg ? "h-8 w-8" : "h-[26px] w-[26px]"} cursor-pointer rounded-full border border-[rgba(16,35,30,0.18)] p-0 hover:scale-110 ${on ? "shadow-[0_0_0_2px_#fff,0_0_0_4px_#0b7a5a]" : ""}`;
const CP_RAINBOW =
  "relative h-[30px] w-[30px] flex-none cursor-pointer rounded-full bg-[conic-gradient(red,yellow,lime,cyan,blue,magenta,red)]";
const PICK_INPUT = "absolute inset-0 cursor-pointer opacity-0";

const Head = ({ title, onBack }: { title: string; onBack?: () => void }) => (
  <div className="mb-3 flex items-center gap-[6px]">
    {onBack && (
      <button className={iconBtnSm} onClick={onBack} aria-label="Back">
        <Icon name="back" size={16} />
      </button>
    )}
    <h2 className={`${DISPLAY} text-[19px] font-bold`}>{title}</h2>
  </div>
);

export const RAIL: ReadonlyArray<readonly [string, string, IconName]> = [
  ["templates", "Design", "template"],
  ["elements", "Elements", "shapes"],
  ["text", "Text", "text"],
  ["brand", "Brand", "brand"],
  ["photos", "Photos", "photo"],
  ["draw", "Draw", "draw"],
  ["background", "Background", "background"],
  ["layers", "Layers", "layers"],
];

const GRADIENTS: [string, string][] = [
  ["#0b3d2e", "#0b7a5a"],
  ["#ff7a45", "#d6336c"],
  ["#2b0a3d", "#6a1b4d"],
  ["#1d6fa5", "#38bdf8"],
  ["#f59e0b", "#db2777"],
  ["#111827", "#374151"],
  ["#fde68a", "#fca5a5"],
  ["#a7f3d0", "#93c5fd"],
  ["#ff9a9e", "#fad0c4"],
  ["#a18cd1", "#fbc2eb"],
  ["#f6d365", "#fda085"],
  ["#84fab0", "#8fd3f4"],
  ["#0f2027", "#2c5364"],
  ["#ee0979", "#ff6a00"],
  ["#7f00ff", "#e100ff"],
  ["#00b09b", "#96c93d"],
];
const EMOJI = [
  "🎉",
  "🔥",
  "⭐",
  "❤️",
  "✅",
  "🛍️",
  "🎁",
  "📦",
  "🪔",
  "🌸",
  "☕",
  "🍕",
  "📞",
  "💬",
  "👉",
  "⏰",
  "📍",
  "💯",
  "🎊",
  "🥳",
  "😍",
  "👍",
  "🙏",
  "💥",
  "✨",
  "🎈",
  "🏷️",
  "🚚",
  "💐",
  "🍰",
  "🧁",
  "🎂",
];
const FEATURED_ICONS =
  "Phone MessageCircle Mail MapPin Clock Calendar ShoppingBag ShoppingCart Gift Tag Percent Star Heart ThumbsUp CircleCheck BadgeCheck Truck Package CreditCard Wallet IndianRupee Store Utensils Coffee Pizza Cake Shirt Scissors Sparkles Flame Zap Award Crown Gem Leaf Sun Bell Camera Music Globe Instagram Facebook Youtube ArrowRight Megaphone Rocket Smile Users Stethoscope GraduationCap House Car Dumbbell PawPrint Headphones Lightbulb Trophy Plane".split(
    " ",
  );

const errText = (e: unknown): string =>
  typeof e === "string"
    ? e
    : e instanceof Error
      ? e.message
      : "Something went wrong";

export function Panels({ ed }: { ed: EditorApi }) {
  switch (ed.panel) {
    case "templates":
      return <TemplatesPanel ed={ed} />;
    case "elements":
      return <ElementsPanel ed={ed} />;
    case "text":
      return <TextPanel ed={ed} />;
    case "brand":
      return <BrandPanel ed={ed} />;
    case "photos":
      return <PhotosPanel ed={ed} />;
    case "draw":
      return <DrawPanel ed={ed} />;
    case "background":
      return <BackgroundPanel ed={ed} />;
    case "layers":
      return <LayersPanel ed={ed} />;
    case "adjust":
      return <AdjustPanel ed={ed} />;
    case "effects":
      return <EffectsPanel ed={ed} />;
    case "position":
      return <PositionPanel ed={ed} />;
    default:
      return null;
  }
}

// ================================================================ templates

const previewCache = new Map<string | number, string | null>();
const TPL_GRID = "grid grid-cols-2 items-start gap-[10px]";

function TemplatesPanel({ ed }: { ed: EditorApi }) {
  const { design } = ed;
  const [list, setList] = useState<Template[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previews, setPreviews] = useState<Record<string, string | null>>({});
  const [q, setQ] = useState("");
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    dispatch(fetchStudioTemplates())
      .unwrap()
      .then((all) =>
        setList(
          ((all || []) as unknown as Template[]).filter(
            (t) => t.width === design?.width && t.height === design?.height,
          ),
        ),
      )
      .catch((e: unknown) => {
        setError(errText(e));
        setList([]);
      });
  }, [design?.width, design?.height, dispatch]);

  useEffect(() => {
    let stop = false;
    (async () => {
      for (const t of list || []) {
        if (stop) return;
        if (!previewCache.has(t.id)) {
          try {
            const spec = JSON.parse(t.specJson);
            // eslint-disable-next-line no-await-in-loop
            await loadFonts(
              (spec.layers || []).map(
                (l: { fontFamily: string }) => l.fontFamily,
              ),
            );
            // eslint-disable-next-line no-await-in-loop
            previewCache.set(
              t.id,
              t.thumbnailUrl ||
                (await renderSpecPreview(spec, t.width, t.height, 260)),
            );
          } catch {
            previewCache.set(t.id, null);
          }
        }
        setPreviews((p) => ({ ...p, [t.id]: previewCache.get(t.id) ?? null }));
      }
    })();
    return () => {
      stop = true;
    };
  }, [list]);

  async function use(t: Template, mode: "add" | "replace") {
    const spec = JSON.parse(t.specJson);
    await loadFonts(
      (spec.layers || []).map((l: { fontFamily: string }) => l.fontFamily),
    );
    if (mode === "add") await ed.addPage(null);
    const c = ed.canvas;
    c.discardActiveObject();
    c.clear();
    buildFromSpec(c, spec, design?.width ?? 0, design?.height ?? 0);
    ed.changed();
    ed.refresh();
    ed.notify(
      mode === "add"
        ? "Template added as a new page."
        : "Page replaced with the template. Press Ctrl+Z to undo.",
    );
  }

  const shown = (list || []).filter(
    (t) =>
      !q || `${t.name} ${t.category}`.toLowerCase().includes(q.toLowerCase()),
  );

  return (
    <div className={PANEL}>
      <Head title="Templates" />
      <SearchBox value={q} onChange={setQ} placeholder="Search templates" />
      {error && <p className={ERROR}>{error}</p>}
      {list === null && (
        <div className={TPL_GRID}>
          {Array.from({ length: 4 }).map((_, i) => (
            <span key={i} className={SKEL} />
          ))}
        </div>
      )}
      {list && list.length === 0 && !error && (
        <p className={HINT_TOP}>
          No templates for {design?.width} × {design?.height} yet. Use{" "}
          <strong>Resize</strong> to switch to a standard size, or start from
          the Studio home.
        </p>
      )}
      <div className={TPL_GRID}>
        {shown.map((t) => (
          <div
            key={t.id}
            className="group relative overflow-hidden rounded-10 bg-st-paper ring-1 ring-st-line"
            style={{ aspectRatio: `${t.width} / ${t.height}` }}
          >
            {previews[t.id] ? (
              <img
                className="block h-full w-full object-cover"
                src={previews[t.id] as string}
                alt=""
              />
            ) : (
              <span className={SKEL_FLAT} />
            )}
            <div className="absolute inset-0 flex flex-col justify-end gap-[5px] bg-[linear-gradient(transparent_30%,rgba(14,28,24,0.82))] p-2 opacity-0 transition-opacity duration-150 group-focus-within:opacity-100 group-hover:opacity-100">
              <span className="truncate text-xs font-semibold text-white">
                {t.name}
              </span>
              <button
                className={btn("accent", "xs")}
                onClick={() => use(t, "add")}
              >
                Add page
              </button>
              <button
                className={btn("plain", "xs")}
                onClick={() => use(t, "replace")}
              >
                Replace page
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

interface SearchBoxProps {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  onEnter?: () => void;
}
function SearchBox({ value, onChange, placeholder, onEnter }: SearchBoxProps) {
  return (
    <label className="mb-3 flex h-10 items-center gap-2 rounded-10 border border-st-line bg-st-paper px-[10px] text-st-muted focus-within:border-st-green focus-within:bg-white">
      <Icon name="search" size={16} />
      <input
        className={`min-w-0 flex-1 border-0 bg-transparent text-sm text-st-ink ${ring}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        onKeyDown={(e) => e.key === "Enter" && onEnter?.()}
      />
      {value && (
        <button
          className="grid cursor-pointer place-items-center border-0 bg-transparent p-1 text-st-muted"
          onClick={() => onChange("")}
          aria-label="Clear"
        >
          <Icon name="close" size={14} />
        </button>
      )}
    </label>
  );
}

// ================================================================ elements

function ElementsPanel({ ed }: { ed: EditorApi }) {
  const { design } = ed;
  const W = design?.width ?? 0;
  const H = design?.height ?? 0;
  const [q, setQ] = useState("");
  const [icons, setIcons] = useState<Record<string, any> | null>(null);
  const [qr, setQr] = useState<{ open: boolean; kind: QrKind; value: string }>({
    open: false,
    kind: "link",
    value: "",
  });
  const brandFill = ed.brand.colors?.[0] || "#0b7a5a";
  const [cat, setCat] = useState<ElementCategory | null>(null);

  // a selected chart or table opens its editor
  const selId = ed.selected?.id;
  useEffect(() => {
    if (ed.selected?.chart) setCat("charts");
    else if (ed.selected?.table) setCat("tables");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selId]);

  useEffect(() => {
    iconLibrary()
      .then(setIcons)
      .catch(() => setIcons({}));
  }, []);

  const iconNames = useMemo(() => {
    if (!icons) return [];
    if (!q.trim()) return FEATURED_ICONS.filter((n) => icons[n]);
    const words = q.toLowerCase().split(/\s+/).filter(Boolean);
    return Object.keys(icons)
      .filter((n) => {
        const hay = n.replace(/([a-z])([A-Z0-9])/g, "$1 $2").toLowerCase();
        return words.every((w) => hay.includes(w));
      })
      .slice(0, 72);
  }, [icons, q]);

  const frame = (shape: FrameShape, radiusK = 0) => {
    const s = Math.min(W, H) * 0.5;
    ed.add(
      makePlaceholder(
        {
          x: (W - s) / 2,
          y: (H - s) / 2,
          w: s,
          h: s,
          radius: s * radiusK,
          shape,
        },
        "Photo",
      ),
    );
  };

  async function addQr() {
    let text = qr.value.trim();
    if (!text) return;
    if (qr.kind === "whatsapp") {
      const digits = text.replace(/\D/g, "");
      if (digits.length < 8) {
        ed.notify(
          "Enter the WhatsApp number with country code, for example 919812345678.",
          "bad",
        );
        return;
      }
      text = `https://wa.me/${digits}`;
    } else if (qr.kind === "link" && !/^https?:\/\//i.test(text)) {
      text = `https://${text}`;
    }
    ed.add(await makeQr(text, W, H));
    setQr({ open: false, kind: qr.kind, value: "" });
  }

  if (cat) {
    return (
      <div className={PANEL}>
        <Head title="Elements" />
        <DesignElements ed={ed} cat={cat} onBack={() => setCat(null)} />
      </div>
    );
  }

  return (
    <div className={PANEL}>
      <Head title="Elements" />
      <SearchBox value={q} onChange={setQ} placeholder="Search 1,900+ icons" />

      {!q && (
        <>
          <div className="mt-3">
            <CategoryTiles onPick={setCat} />
          </div>

          <h3 className={H3}>Shapes</h3>
          <div className={SHAPE_GRID}>
            {SHAPES.map(([k, label]) => (
              <button
                key={k}
                className={SHAPE_BTN}
                onClick={() => ed.add(makeShape(k, W, H, brandFill))}
                aria-label={`Add ${label}`}
                title={label}
              >
                <ShapeIcon kind={k} />
              </button>
            ))}
          </div>

          <h3 className={H3}>Lines and arrows</h3>
          <div className={SHAPE_GRID}>
            {LINES.map(([k, label]) => (
              <button
                key={k}
                className={SHAPE_BTN}
                onClick={() => ed.add(makeLine(k, W, H))}
                aria-label={`Add ${label}`}
                title={label}
              >
                <Glyph kind={`line-${k}`} />
              </button>
            ))}
          </div>

          <h3 className={H3}>Photo frames</h3>
          <div className={SHAPE_GRID}>
            <button
              className={SHAPE_BTN}
              title="Square frame"
              onClick={() => frame("rect")}
            >
              <Glyph kind="frame-rect" />
            </button>
            <button
              className={SHAPE_BTN}
              title="Rounded frame"
              onClick={() => frame("rect", 0.08)}
            >
              <Glyph kind="frame-rounded" />
            </button>
            <button
              className={SHAPE_BTN}
              title="Circle frame"
              onClick={() => frame("circle")}
            >
              <Glyph kind="frame-circle" />
            </button>
            {(
              [
                "arch",
                "heart",
                "hexagon",
                "star",
                "blob",
                "diamond",
              ] as FrameShape[]
            ).map((k) => (
              <button
                key={k}
                className={SHAPE_BTN}
                title={`${k[0].toUpperCase()}${k.slice(1)} frame`}
                onClick={() => frame(k)}
              >
                <FrameGlyph d={FRAME_SHAPES[k] as string} />
              </button>
            ))}
          </div>

          <h3 className={H3}>Photo grids</h3>
          <div className={SHAPE_GRID}>
            {Object.entries(GRIDS).map(([k, cells]) => (
              <button
                key={k}
                className={SHAPE_BTN}
                title={`${cells.length} photos`}
                onClick={() => ed.add(makeGrid(k as GridKind, W, H))}
              >
                <GridGlyph cells={cells} />
              </button>
            ))}
          </div>
          <p className={FINEPRINT}>
            Select a frame, then pick a photo to fill it. Double-click a photo
            to crop it.
          </p>
        </>
      )}

      <h3 className={H3}>{q ? `Icons matching “${q}”` : "Icons"}</h3>
      {!icons && <p className={HINT}>Loading icons…</p>}
      {icons && iconNames.length === 0 && (
        <p className={HINT}>
          No icons found. Try another word, such as “phone”, “gift” or “star”.
        </p>
      )}
      <div className="grid grid-cols-6 gap-1">
        {iconNames.map((n) => (
          <button
            key={n}
            className={`grid aspect-square cursor-pointer place-items-center rounded-lg border-0 bg-transparent p-[6px] text-[#23423a] hover:bg-st-green-soft hover:text-st-green-dark [&_svg]:h-[26px] [&_svg]:w-[26px] ${ring}`}
            title={n.replace(/([a-z])([A-Z0-9])/g, "$1 $2")}
            aria-label={`Add icon ${n}`}
            onClick={async () =>
              ed.add(
                await makeIcon(
                  n,
                  icons![n],
                  W,
                  H,
                  ed.brand.colors?.[0] || "#10231e",
                ),
              )
            }
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: iconSvg(icons?.[n], "currentColor", 1.8),
            }}
          />
        ))}
      </div>

      {!q && (
        <>
          <h3 className={H3}>Stickers</h3>
          <div className="grid grid-cols-8 gap-[6px]">
            {EMOJI.map((e) => (
              <button
                key={e}
                className="aspect-square cursor-pointer rounded-10 border-0 bg-st-paper text-xl hover:scale-[1.06] hover:bg-st-green-soft"
                aria-label={`Add ${e}`}
                onClick={() => {
                  const t = makeText(
                    { size: 0.16, weight: 400, family: "Poppins" },
                    W,
                    H,
                    e,
                  );
                  t.set({
                    name: "Sticker",
                    width: t.fontSize * 1.4,
                    left: (W - t.fontSize * 1.4) / 2,
                  });
                  ed.add(t);
                }}
              >
                {e}
              </button>
            ))}
          </div>

          <h3 className={H3}>QR code</h3>
          {!qr.open ? (
            <button
              className={btn("plain", "md", true)}
              onClick={() => setQr({ ...qr, open: true })}
            >
              <Icon name="qr" size={18} /> Add a QR code
            </button>
          ) : (
            <div className={CARD_SOFT}>
              <Tabs
                value={qr.kind}
                onChange={(kind: string) =>
                  setQr({ ...qr, kind: kind as QrKind })
                }
                items={[
                  ["link", "Website"],
                  ["whatsapp", "WhatsApp chat"],
                  ["text", "Text"],
                ]}
              />
              <input
                className={INPUT}
                autoFocus
                value={qr.value}
                onChange={(e) => setQr({ ...qr, value: e.target.value })}
                placeholder={
                  qr.kind === "whatsapp"
                    ? "919812345678"
                    : qr.kind === "link"
                      ? "yourshop.com/offer"
                      : "Any text"
                }
                aria-label="QR code content"
                onKeyDown={(e) => e.key === "Enter" && addQr()}
              />
              {qr.kind === "whatsapp" && (
                <p className={FINEPRINT}>
                  Scanning opens a WhatsApp chat with this number.
                </p>
              )}
              <div className={ROW_END}>
                <button
                  className={btn("plain", "sm")}
                  onClick={() => setQr({ ...qr, open: false })}
                >
                  Cancel
                </button>
                <button
                  className={btn("accent", "sm")}
                  disabled={!qr.value.trim()}
                  onClick={addQr}
                >
                  Add QR code
                </button>
              </div>
            </div>
          )}

          {(ed.brand.logos?.length ?? 0) > 0 && (
            <>
              <h3 className={H3}>Your logos</h3>
              <div className={MEDIA_GRID_3}>
                {ed.brand.logos!.map((l) => (
                  <button
                    key={l.galleryId}
                    className={MEDIA_CONTAIN}
                    onClick={() =>
                      ed.insertImage({
                        id: l.galleryId,
                        mediaUrl: l?.url ?? "",
                      })
                    }
                  >
                    <img className={MEDIA_IMG} src={l.url} alt="Logo" />
                  </button>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function isGlyphKind(kind: string): kind is GlyphKind {
  return [
    "rect",
    "rounded",
    "pill",
    "circle",
    "triangle",
    "star",
    "line-solid",
    "line-dashed",
    "line-dotted",
    "line-arrow",
    "line-double",
    "frame-rect",
    "frame-rounded",
    "frame-circle",
  ].includes(kind);
}

function ShapeIcon({ kind }: { kind: string }) {
  const pathShapes: Record<string, string> = {
    arrow: "M0 30 L60 30 L60 0 L100 50 L60 100 L60 70 L0 70 Z",
    heart: FRAME_SHAPES.heart,
    bubble:
      "M12 0 L88 0 C95 0 100 5 100 12 L100 62 C100 69 95 74 88 74 L40 74 L18 100 L22 74 L12 74 C5 74 0 69 0 62 L0 12 C0 5 5 0 12 0 Z",
    hexagon: FRAME_SHAPES.hexagon,
    pentagon: "M50 0 L100 38 L81 100 L19 100 L0 38 Z",
    diamond: FRAME_SHAPES.diamond,
    cross:
      "M35 0 L65 0 L65 35 L100 35 L100 65 L65 65 L65 100 L35 100 L35 65 L0 65 L0 35 L35 35 Z",
    parallelogram: "M25 0 L100 0 L75 100 L0 100 Z",
    chevron: "M0 0 L70 0 L100 50 L70 100 L0 100 L30 50 Z",
    arch: FRAME_SHAPES.arch,
    blob: FRAME_SHAPES.blob,
  };

  if (kind === "badge") {
    return (
      <svg viewBox="0 0 48 48" width="40" height="40" aria-hidden="true">
        <path d={burst()} fill="currentColor" />
      </svg>
    );
  }
  if (pathShapes[kind]) return <Glyph d={pathShapes[kind]} />;
  if (isGlyphKind(kind)) return <Glyph kind={kind} />;
  return null;
}

function burst(): string {
  const pts: string[] = [];
  for (let i = 0; i < 32; i++) {
    const r = i % 2 === 0 ? 21 : 17;
    const a = (Math.PI / 16) * i - Math.PI / 2;
    pts.push(
      `${(24 + r * Math.cos(a)).toFixed(1)} ${(24 + r * Math.sin(a)).toFixed(1)}`,
    );
  }
  return `M${pts.join(" L")} Z`;
}

// ================================================================ text

const TEXT_PRESET_BTN = `mb-2 block w-full cursor-pointer rounded-10 border border-st-line bg-st-paper px-[14px] py-3 text-left font-['Poppins',sans-serif] text-st-ink hover:border-st-green ${ring}`;
const FONT_CARD = `min-h-[74px] cursor-pointer rounded-10 border border-st-line bg-white p-2 text-xl leading-[1.1] text-st-ink hover:border-st-green hover:bg-st-green-soft ${ring}`;

function TextPanel({ ed }: { ed: EditorApi }) {
  const { design, brand } = ed;
  const W = design?.width ?? 0;
  const H = design?.height ?? 0;

  useEffect(() => {
    loadFonts([]);
  }, []);

  async function addPreset(p: any, text?: string) {
    await loadFonts([p.family]);
    ed.add(ed.makeText(p, W, H, text));
  }
  async function addCombo(combo: any) {
    await loadFonts([combo.top.family, combo.bottom.family]);
    ed.add(ed.makeCombo(combo, W, H));
  }
  const heading = brand.headingFont || "Poppins";
  const body = brand.bodyFont || "Poppins";

  return (
    <div className={PANEL}>
      <Head title="Text" />
      <button
        className={btn("accent", "md", true)}
        onClick={() =>
          addPreset({ ...TEXT_PRESETS[2], family: body }, "Add your text")
        }
      >
        <Icon name="text" size={18} /> Add a text box
      </button>
      <h3 className={H3}>Default text styles</h3>
      <button
        className={`${TEXT_PRESET_BTN} text-2xl font-bold`}
        style={{ fontFamily: `"${heading}"` }}
        onClick={() => addPreset({ ...TEXT_PRESETS[0], family: heading })}
      >
        Add a heading
      </button>
      <button
        className={`${TEXT_PRESET_BTN} text-[17px] font-semibold`}
        style={{ fontFamily: `"${heading}"` }}
        onClick={() => addPreset({ ...TEXT_PRESETS[1], family: heading })}
      >
        Add a subheading
      </button>
      <button
        className={`${TEXT_PRESET_BTN} text-[13px]`}
        style={{ fontFamily: `"${body}"` }}
        onClick={() => addPreset({ ...TEXT_PRESETS[2], family: body })}
      >
        Add a little bit of body text
      </button>
      {(brand.headingFont || brand.bodyFont) && (
        <p className={FINEPRINT}>
          <Icon name="brand" size={12} /> Using your brand fonts.
        </p>
      )}

      <h3 className={H3}>Font combinations</h3>
      <div className="grid grid-cols-2 gap-2">
        {TEXT_COMBOS.map((cb: any) => (
          <button
            key={cb.key}
            className={`${FONT_CARD} flex flex-col items-center justify-center gap-1`}
            onClick={() => addCombo(cb)}
          >
            <span
              className="text-[19px] leading-none"
              style={{
                fontFamily: `"${cb.top.family}"`,
                fontWeight: cb.top.weight,
              }}
            >
              {cb.top.text}
            </span>
            <small
              className="text-[9.5px] text-st-muted"
              style={{
                fontFamily: `"${cb.bottom.family}"`,
                fontWeight: cb.bottom.weight,
                letterSpacing: cb.bottom.charSpacing ? "0.2em" : 0,
              }}
            >
              {cb.bottom.text}
            </small>
          </button>
        ))}
        {TEXT_PRESETS.slice(3).map((p: any) => (
          <button
            key={p.key}
            className={FONT_CARD}
            style={{ fontFamily: `"${p.family}"`, fontWeight: p.weight }}
            onClick={() => addPreset(p)}
          >
            {p.label}
          </button>
        ))}
      </div>
      <p className={HINT}>
        Double-click text on the design to type. Hindi, Marathi and other Indian
        scripts work with the fonts in the “Indian” group.
      </p>
    </div>
  );
}

// ================================================================ brand kit

function BrandPanel({ ed }: { ed: EditorApi }) {
  const [kit, setKit] = useState<Brand>(ed.brand);
  const [picking, setPicking] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => setKit(ed.brand), [ed.brand]);

  async function update(patch: Partial<Brand>) {
    const next = { ...kit, ...patch };
    setKit(next);
    setSaving(true);
    await ed.saveBrand(next);
    setSaving(false);
  }

  return (
    <div className={PANEL}>
      <Head title="Brand kit" />
      <p className={HINT_TOP}>
        Your colours, fonts and logos, ready in every design.{" "}
        {saving && <em>Saving…</em>}
      </p>

      <h3 className={H3}>Colours</h3>
      <div className="flex flex-wrap gap-2">
        {(kit.colors || []).map((c, i) => (
          <span
            key={`${c}${i}`}
            className="group relative h-10 w-10 rounded-10 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.12)]"
            style={{ background: c }}
            title={c}
          >
            <button
              className="absolute -right-[6px] -top-[6px] grid h-5 w-5 cursor-pointer place-items-center rounded-full border-0 bg-st-ink text-white opacity-0 focus-visible:opacity-100 group-hover:opacity-100"
              onClick={() =>
                update({ colors: kit.colors!.filter((_, j) => j !== i) })
              }
              aria-label={`Remove ${c}`}
            >
              <Icon name="close" size={12} />
            </button>
          </span>
        ))}
        {(kit.colors || []).length < 16 && (
          <label
            className="relative grid h-10 w-10 cursor-pointer place-items-center rounded-10 border-[1.5px] border-dashed border-[#9fb3ab] text-st-muted"
            title="Add a colour"
          >
            <Icon name="plus" size={18} />
            <input
              className={PICK_INPUT}
              type="color"
              defaultValue="#0b7a5a"
              onChange={(e) => {
                e.currentTarget.dataset.v = e.currentTarget.value;
              }}
              onBlur={(e) => {
                const el = e.currentTarget;
                if (el.dataset.v) {
                  update({ colors: [...(kit.colors || []), el.dataset.v] });
                  el.dataset.v = "";
                }
              }}
              aria-label="Add a brand colour"
            />
          </label>
        )}
      </div>
      <p className={FINEPRINT}>
        These colours show first in every colour picker.
      </p>

      <h3 className={H3}>Fonts</h3>
      <label className={FIELD}>
        <span>Headings</span>
        <FontSelect
          className="w-full"
          value={kit.headingFont || ""}
          onChange={(f) => update({ headingFont: f || null })}
          empty="Not set"
        />
      </label>
      <label className={FIELD}>
        <span>Body text</span>
        <FontSelect
          className="w-full"
          value={kit.bodyFont || ""}
          onChange={(f) => update({ bodyFont: f || null })}
          empty="Not set"
        />
      </label>
      <button
        className={btn("plain", "sm", true)}
        disabled={!kit.headingFont && !kit.bodyFont}
        onClick={ed.applyBrandFonts}
      >
        Use brand fonts on this page
      </button>

      <h3 className={H3}>Logos</h3>
      <div className={MEDIA_GRID_3}>
        {(kit.logos || []).map((l) => (
          <div key={l.galleryId} className={MEDIA_CONTAIN}>
            <button
              className="absolute inset-0 cursor-pointer border-0 bg-transparent p-0"
              onClick={() =>
                ed.insertImage({ id: l.galleryId, mediaUrl: l.url ?? "" })
              }
              title="Add to the design"
            >
              <img className={MEDIA_IMG} src={l.url} alt="Logo" />
            </button>
            <button
              className="absolute right-1 top-1 grid h-[22px] w-[22px] cursor-pointer place-items-center rounded-full border-0 bg-st-ink/75 text-white opacity-0 group-hover:opacity-100"
              onClick={() =>
                update({
                  logos: kit.logos!.filter((x) => x.galleryId !== l.galleryId),
                })
              }
              aria-label="Remove logo"
            >
              <Icon name="close" size={12} />
            </button>
          </div>
        ))}
      </div>
      {(kit.logos || []).length < 10 &&
        (picking ? (
          <>
            <MediaPicker
              type="IMAGE"
              compact
              onPick={(it: { id: string | number; mediaUrl: string }) => {
                setPicking(false);
                update({
                  logos: [
                    ...(kit.logos || []),
                    { galleryId: it.id, url: it.mediaUrl },
                  ],
                });
              }}
            />
            <button className={linkBlock} onClick={() => setPicking(false)}>
              Cancel
            </button>
          </>
        ) : (
          <button
            className={`${btn("plain", "sm", true)} mt-[10px]`}
            onClick={() => setPicking(true)}
          >
            <Icon name="plus" size={16} /> Add a logo
          </button>
        ))}
      <p className={FINEPRINT}>
        Tip: upload a transparent PNG logo so it sits nicely on any colour.
      </p>
    </div>
  );
}

interface FontSelectProps {
  value: string;
  onChange: (f: string) => void;
  empty?: string;
  style?: React.CSSProperties;
  className?: string;
}
export function FontSelect({
  value,
  onChange,
  empty,
  style,
  className = "",
}: FontSelectProps) {
  const groups = useMemo(
    () =>
      FONTS.reduce(
        (m: Record<string, string[]>, f: { group: string; family: string }) => {
          (m[f.group] = m[f.group] || []).push(f.family);
          return m;
        },
        {},
      ),
    [],
  );
  return (
    <select
      className={`${SELECT} ${className}`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={style}
      aria-label="Font"
    >
      {empty && <option value="">{empty}</option>}
      {Object.entries(groups).map(([g, list]) => (
        <optgroup key={g} label={g}>
          {list.map((f) => (
            <option key={f} value={f} style={{ fontFamily: `"${f}"` }}>
              {f}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

function galleryPostToMediaRef(p: GalleryPost): MediaRef {
  return { id: p.id, mediaUrl: p.mediaUrl };
}

const uploadMatches = (
  fileName: string | null,
  templateName: string | null,
  q: string,
) => `${fileName || ""} ${templateName || ""}`.toLowerCase().includes(q);

/** Searches the user's uploads, then falls back to Gallery, for a given query. */
function MatchedUploadsFallback({
  ed,
  query,
}: {
  ed: EditorApi;
  query: string;
}) {
  const [state, setState] = useState<{
    loading: boolean;
    results: MediaRef[];
    error: string | null;
    source: "uploads" | "gallery" | null;
  }>({ loading: true, results: [], error: null, source: null });
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    let cancelled = false;
    const q = query.trim().toLowerCase();
    if (!q) {
      setState({ loading: false, results: [], error: null, source: null });
      return;
    }

    (async () => {
      setState({ loading: true, results: [], error: null, source: null });
      try {
        let found: MediaRef[] = [];
        const MAX_PAGES = 4;
        for (let p = 0; p < MAX_PAGES; p++) {
          const data = (await dispatch(
            fetchStudioUploads({ type: "IMAGE", page: p, size: 30 }),
          ).unwrap()) as StudioUploadsResponse;
          const list = data?.results || [];
          found = found.concat(
            list.filter((it) => uploadMatches(it.fileName, it.templateName, q)),
          );
          const total = data?.page?.total ?? 0;
          if (list.length === 0 || (p + 1) * 30 >= total) break;
        }
        if (cancelled) return;
        if (found.length > 0) {
          setState({
            loading: false,
            results: found,
            error: null,
            source: "uploads",
          });
          return;
        }

        const g = (await dispatch(
          fetchGalleryPosts({
            mediaType: "IMAGE",
            templateName: query.trim(),
            page: 0,
            size: 30,
          }),
        ).unwrap()) as GallerySearchResponse;
        if (cancelled) return;
        setState({
          loading: false,
          results: (g.results || []).map(galleryPostToMediaRef),
          error: null,
          source: "gallery",
        });
      } catch (e) {
        if (!cancelled) {
          setState({
            loading: false,
            results: [],
            error: errText(e),
            source: null,
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [query, dispatch]);

  if (state.loading) {
    return (
      <div className="grid grid-cols-3 gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <span key={i} className={`${SKEL} aspect-square`} />
        ))}
      </div>
    );
  }
  if (state.error) return <p className={ERROR}>{state.error}</p>;
  if (state.results.length === 0) {
    return (
      <p className={HINT}>
        No matches for "{query}" in your uploads or Gallery either.
      </p>
    );
  }

  return (
    <>
      <p className={FINEPRINT}>
        {state.source === "uploads"
          ? "From your uploads:"
          : "Not in uploads — from your Gallery:"}
      </p>
      <div className={MEDIA_GRID_3}>
        {state.results.map((it) => (
          <button
            key={it.id}
            className={MEDIA_CONTAIN}
            onClick={() => ed.insertImage(it)}
          >
            <img className={MEDIA_IMG} src={it.mediaUrl} alt="" />
          </button>
        ))}
      </div>
    </>
  );
}

// ================================================================ photos (Gallery + free stock)

function PhotosPanel({ ed }: { ed: EditorApi }) {
  const [tab, setTab] = useState("uploads");
  const o = ed.selected;
  const filling = o?.placeholder || (ed.kind === "image" && o?.frame);
  return (
    <div className={PANEL}>
      <Head title="Photos" />
      {ed.features.stockPhotos && (
        <Tabs
          value={tab}
          onChange={setTab}
          items={[
            ["uploads", "Your uploads"],
            ["stock", "Free photos"],
          ]}
        />
      )}
      {filling ? (
        <p className={CALLOUT}>Pick a photo to fill the selected frame.</p>
      ) : (
        <p className={HINT_TOP}>
          Click a photo to add it. Select a frame first to fill it.
        </p>
      )}
      {tab === "uploads" && (
        <MediaPicker
          type="IMAGE"
          onPick={(it: MediaRef) => ed.insertImage(it)}
          busy={ed.busy === "image"}
        />
      )}
      {tab === "stock" && <StockPhotos ed={ed} />}
    </div>
  );
}

const CHIP =
  "cursor-pointer rounded-full border px-[13px] py-[7px] text-[13px] font-medium";
const MASONRY_CHILD = "mb-2 break-inside-avoid";

function StockPhotos({ ed }: { ed: EditorApi }) {
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [orientation, setOrientation] = useState("");
  const [data, setData] = useState<{
    photos: StockPhoto[] | null;
    page: number;
    hasMore: boolean;
  }>({ photos: null, page: 1, hasMore: false });
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState<string | number | null>(null);
  const dispatch = useDispatch<AppDispatch>();

  async function load(page: number) {
    setError(null);
    try {
      const r = (await dispatch(
        fetchStudioStockPhotos({
          q: query || undefined,
          orientation: orientation || undefined,
          page,
        }),
      ).unwrap()) as { photos: StockPhoto[]; hasMore: boolean };
      setData((d) => ({
        photos: page === 1 ? r.photos : [...(d.photos || []), ...r.photos],
        page,
        hasMore: r.hasMore,
      }));
    } catch (e) {
      setError((e as Error).message);
      setData((d) => ({ ...d, photos: d.photos || [] }));
    }
  }

  useEffect(() => {
    setData({ photos: null, page: 1, hasMore: false });
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, orientation]);

  return (
    <div>
      <SearchBox
        value={q}
        onChange={setQ}
        placeholder="Search free photos"
        onEnter={() => setQuery(q.trim())}
      />
      <div className="mb-[10px] flex flex-wrap gap-[6px]">
        {[
          ["", "Any"],
          ["square", "Square"],
          ["portrait", "Tall"],
          ["landscape", "Wide"],
        ].map(([k, l]) => (
          <button
            key={k}
            className={`${CHIP} ${orientation === k ? "border-st-ink bg-st-ink text-white" : "border-st-line bg-white text-st-ink"}`}
            onClick={() => setOrientation(k)}
          >
            {l}
          </button>
        ))}
      </div>
      {error && (
        <p className={ERROR} role="alert">
          {error}
        </p>
      )}
      {data.photos === null && (
        <div className="columns-2 gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <span
              key={i}
              className={`${SKEL} ${MASONRY_CHILD}`}
              style={{ height: 90 + (i % 3) * 30 }}
            />
          ))}
        </div>
      )}

      {data.photos && data.photos.length === 0 && !error && (
        <>
          <p className={HINT}>
            No free photos found for "{query}". Checking your uploads…
          </p>
          <MatchedUploadsFallback ed={ed} query={query} />
        </>
      )}

      <div className="columns-2 gap-2">
        {(data.photos || []).map((p) => (
          <button
            key={p.id}
            className={`${MASONRY_CHILD} group relative block w-full cursor-pointer overflow-hidden rounded-10 border-0 bg-st-paper p-0 hover:ring-2 hover:ring-st-green`}
            disabled={!!adding}
            onClick={async () => {
              setAdding(p.id);
              await ed.insertStockPhoto(p);
              setAdding(null);
            }}
            title={p.alt || "Stock photo"}
          >
            <img
              className="block w-full"
              src={p.thumbUrl}
              alt={p.alt || ""}
              loading="lazy"
              style={{ aspectRatio: `${p.width} / ${p.height}` }}
            />
            <span className="absolute inset-x-0 bottom-0 bg-[linear-gradient(transparent,rgba(0,0,0,0.6))] px-2 pb-[6px] pt-[14px] text-left text-[11px] text-white opacity-0 transition-opacity duration-150 group-hover:opacity-100">
              {adding === p.id ? "Adding…" : p.photographer}
            </span>
          </button>
        ))}
      </div>
      {data.hasMore && (
        <button
          className={`${link} mt-[10px]`}
          onClick={() => load(data.page + 1)}
        >
          Show more
        </button>
      )}
      <p className={FINEPRINT}>
        Photos by{" "}
        <a href="https://www.pexels.com" target="_blank" rel="noreferrer">
          Pexels
        </a>
        , free to use. A chosen photo is saved to your Gallery.
      </p>
    </div>
  );
}

// ================================================================ draw

const DRAW_TOOL =
  "grid cursor-pointer justify-items-center gap-2 rounded-xl border px-1 py-3 text-[13px] font-semibold text-st-ink";
const DRAW_TIP: Record<DrawKind, string> = {
  pen: "h-10 w-[10px] rounded-b-[6px] rounded-t-[3px]",
  marker: "h-10 w-[16px] rounded-b-[6px] rounded-t-[3px]",
  highlighter: "h-10 w-[22px] rounded-sm opacity-[0.45]",
};

function DrawPanel({ ed }: { ed: EditorApi }) {
  const [kind, setKind] = useState<DrawKind>("pen");
  const [color, setColor] = useState(ed.brand.colors?.[0] || "#10231e");
  const [size, setSize] = useState(8);
  const minSide = Math.min(ed.design?.width ?? 0, ed.design?.height ?? 0);

  useEffect(() => {
    const k = {
      pen: 1,
      marker: 2.6,
      highlighter: 5,
    }[kind];

    const drawingKind: "pen" | "highlighter" =
      kind === "highlighter" ? "highlighter" : "pen";

    ed.setDrawing({
      kind: drawingKind,
      color,
      width: Math.max(1, (size * k * minSide) / 1080),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, color, size]);

  return (
    <div className={PANEL}>
      <Head title="Draw" />
      <div className="grid grid-cols-3 gap-2">
        {(
          [
            ["pen", "Pen"],
            ["marker", "Marker"],
            ["highlighter", "Highlighter"],
          ] as [DrawKind, string][]
        ).map(([k, l]) => (
          <button
            key={k}
            className={`${DRAW_TOOL} ${kind === k ? "border-st-green bg-st-green-soft shadow-[inset_0_0_0_1px_#0b7a5a]" : "border-st-line bg-white"}`}
            onClick={() => setKind(k)}
            aria-pressed={kind === k}
          >
            <span className={DRAW_TIP[k]} style={{ background: color }} /> {l}
          </button>
        ))}
      </div>
      <h3 className={H3}>Colour</h3>
      <div className={CP_ROW}>
        {[...(ed.brand.colors || []), ...PALETTE.slice(0, 18)].map(
          (c: string, i: number) => (
            <button
              key={`${c}${i}`}
              className={cpSw(c === color)}
              style={{ background: c }}
              onClick={() => setColor(c)}
              aria-label={c}
            />
          ),
        )}
        <label className={CP_RAINBOW}>
          <input
            className={PICK_INPUT}
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            aria-label="Any colour"
          />
        </label>
      </div>
      <Slider
        label="Thickness"
        value={size}
        min={1}
        max={40}
        onChange={setSize}
      />
      <p className={HINT}>
        Draw on the page. Each stroke becomes its own element you can move,
        recolour or delete. Pick another tool on the left to stop drawing.
      </p>
    </div>
  );
}

// ================================================================ background

function BackgroundPanel({ ed }: { ed: EditorApi }) {
  const c = ed.canvas;
  const [angle, setAngle] = useState(135);
  const [grad, setGrad] = useState<[string, string] | null>(null);
  const bg = typeof c.backgroundColor === "string" ? c.backgroundColor : null;

  return (
    <div className={PANEL}>
      <Head title="Background" />
      <h3 className={H3}>Colour</h3>
      <div className={CP_ROW}>
        <ColorPicker
          value={bg || "#ffffff"}
          onChange={(v: string | null) => ed.setBackground(v || "#ffffff")}
          label="Background colour"
          brandColors={ed.brand.colors || []}
        />
        {(ed.brand.colors || []).map((s) => (
          <button
            key={`b${s}`}
            className={cpSw(false, true)}
            style={{ background: s }}
            onClick={() => ed.setBackground(s)}
            aria-label={`Brand colour ${s}`}
          />
        ))}
        {[
          "#ffffff",
          "#f5efe6",
          "#10231e",
          "#0b7a5a",
          "#25d366",
          "#1d6fa5",
          "#7c3aed",
          "#db2777",
          "#e11d48",
          "#f97316",
          "#ffd166",
          "#000000",
        ].map((s) => (
          <button
            key={s}
            className={cpSw(bg === s, true)}
            style={{ background: s }}
            onClick={() => ed.setBackground(s)}
            aria-label={`Background ${s}`}
          />
        ))}
      </div>
      <h3 className={H3}>Gradient</h3>
      <div className="grid grid-cols-6 gap-2">
        {GRADIENTS.map((g) => (
          <button
            key={g.join()}
            className="aspect-square w-full cursor-pointer rounded-xl border border-[rgba(16,35,30,0.18)] p-0"
            style={{
              background: `linear-gradient(${angle}deg, ${g[0]}, ${g[1]})`,
            }}
            onClick={() => {
              setGrad(g);
              ed.setBackground(g, angle);
            }}
            aria-label={`Gradient ${g.join(" to ")}`}
          />
        ))}
      </div>
      <Slider
        label="Gradient angle"
        value={angle}
        min={0}
        max={360}
        suffix="°"
        onChange={(a: number) => {
          setAngle(a);
          if (grad) ed.setBackground(grad, a);
        }}
      />
      <h3 className={H3}>Photo</h3>
      {c.getObjects().some((x: any) => x.isBackgroundImage) && (
        <button className={linkBlock} onClick={ed.removeBackgroundPhoto}>
          Remove background photo
        </button>
      )}
      <MediaPicker
        type="IMAGE"
        onPick={ed.setBackgroundPhoto}
        busy={ed.busy === "image"}
        compact
      />
    </div>
  );
}

// ================================================================ layers

function LayersPanel({ ed }: { ed: EditorApi }) {
  const c = ed.canvas;
  const [drag, setDrag] = useState<number | null>(null);
  const objects: any[] = c.getObjects();
  const shown = [...objects].reverse();
  const active = new Set(c.getActiveObjects());

  return (
    <div className={PANEL}>
      <Head title="Layers" />
      <p className={HINT_TOP}>Top of the list is in front. Drag to reorder.</p>
      <ol className="grid list-none grid-cols-[minmax(0,1fr)] gap-1 p-0">
        {shown.map((x, i) => (
          <li
            key={x.id || i}
            className={`flex cursor-grab items-center gap-1 rounded-10 border pr-1 ${active.has(x) ? "border-st-green bg-st-green-soft" : "border-transparent hover:bg-st-paper"}${drag === i ? " opacity-40" : ""}`}
            draggable={!x.isBackgroundImage}
            onDragStart={() => setDrag(i)}
            onDragEnd={() => setDrag(null)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => {
              if (drag !== null && drag !== i)
                ed.moveLayer(shown[drag], objects.length - 1 - i);
              setDrag(null);
            }}
          >
            <span
              className="pl-[6px] text-[11px] tracking-[-2px] text-[#b4c4bd]"
              aria-hidden="true"
            >
              ⋮⋮
            </span>
            <button
              className={`flex min-w-0 flex-1 cursor-pointer items-center gap-[10px] border-0 bg-transparent px-[10px] py-2 text-left text-st-ink${x.visible === false ? " opacity-[0.45]" : ""}`}
              onClick={() => ed.selectLayer(x)}
            >
              <LayerThumb o={x} />
              <span className="truncate text-[13px]">{layerName(x)}</span>
            </button>
            <button
              className={iconBtnSm}
              onClick={() => ed.toggleVisible(x)}
              aria-label={x.visible === false ? "Show" : "Hide"}
              title={x.visible === false ? "Show" : "Hide"}
            >
              <Icon name={x.visible === false ? "eyeOff" : "eye"} size={16} />
            </button>
            <button
              className={iconBtnSm}
              onClick={() => ed.toggleLock(x)}
              aria-label={x.locked ? "Unlock" : "Lock"}
              title={x.locked ? "Unlock" : "Lock"}
            >
              <Icon name={x.locked ? "lock" : "unlock"} size={16} />
            </button>
          </li>
        ))}
      </ol>
      {objects.length === 0 && (
        <p className={HINT}>Nothing on this page yet.</p>
      )}
    </div>
  );
}

function LayerThumb({ o }: { o: any }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    try {
      const b = o.getBoundingRect();
      setUrl(
        o.toDataURL({
          format: "png",
          multiplier:
            56 / Math.max(b.width, b.height, 1) / (o.canvas?.getZoom() || 1),
        }),
      );
    } catch {
      setUrl(null);
    }
  }, [o, o.fill, o.text, o.src]);

  return (
    <span className="grid h-10 w-10 flex-none place-items-center overflow-hidden rounded-[7px] border border-st-line text-[13px] font-bold text-st-muted [background:repeating-conic-gradient(#f0f4f2_0_25%,#fff_0_50%)_0_0/10px_10px]">
      {url ? (
        <img
          className="max-h-full max-w-full object-contain"
          src={url}
          alt=""
        />
      ) : (
        "◆"
      )}
    </span>
  );
}

function layerName(o: any): string {
  if (o.isBackgroundImage) return "Background photo";
  const t = (o.type || "").toLowerCase();
  if (t === "textbox")
    return (o.text || "Text").split("\n")[0].slice(0, 28) || "Text";
  return o.name || t;
}

// ================================================================ edit photo (looks, adjustments, effects)

function AdjustPanel({ ed }: { ed: EditorApi }) {
  const img = ed.kind === "image" ? ed.selected : null;
  const [look, setLook] = useState<Look | null>(() =>
    img ? readLook(img) : null,
  );
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [tab, setTab] = useState("filters");
  const [pixelEraserOn, setPixelEraserOn] = useState(false);
  const [brushSize, setBrushSize] = useState(24);
  const [borderColor, setBorderColor] = useState("#ffffff");
  const [borderWidth, setBorderWidth] = useState(0);
  const [borderStyle, setBorderStyle] = useState<"solid" | "dashed" | "dotted">(
    "solid",
  );

  useEffect(() => {
    if (!img) return undefined;
    setLook(readLook(img));
    let stop = false;
    setPreviews({});
    lookPreviews(img)
      .then((p: Record<string, string>) => {
        if (!stop) setPreviews(p);
      })
      .catch(() => {});
    return () => {
      stop = true;
    };
  }, [img, img?.assetUrl]);

  useEffect(() => {
    const canvas = ed.canvas;

    if (!pixelEraserOn || !img || !canvas) {
      return;
    }

    if (
      !img.canvas ||
      img.canvas !== canvas ||
      !canvas.getObjects().includes(img)
    ) {
      ed.notify("Please select an image on the canvas.", "bad");
      setPixelEraserOn(false);
      return;
    }

    // Get the selected photo's source safely.
    const source = img.getElement?.();

    if (!source) {
      ed.notify("Unable to load the selected image.", "bad");
      setPixelEraserOn(false);
      return;
    }

    if (
      !(source instanceof HTMLImageElement) &&
      !(source instanceof HTMLCanvasElement) &&
      !(source instanceof HTMLVideoElement)
    ) {
      ed.notify("Unsupported image source.", "bad");
      setPixelEraserOn(false);
      return;
    }

    const sourceWidth =
      source instanceof HTMLImageElement
        ? source.naturalWidth
        : source instanceof HTMLVideoElement
          ? source.videoWidth
          : source.width;

    const sourceHeight =
      source instanceof HTMLImageElement
        ? source.naturalHeight
        : source instanceof HTMLVideoElement
          ? source.videoHeight
          : source.height;

    if (!sourceWidth || !sourceHeight) {
      ed.notify("The image has no valid dimensions.", "bad");
      setPixelEraserOn(false);
      return;
    }

    // Create a separate canvas so we don't erase the main design canvas.
    const workCanvas = document.createElement("canvas");
    workCanvas.width = sourceWidth;
    workCanvas.height = sourceHeight;

    const ctx = workCanvas.getContext("2d");

    if (!ctx) {
      ed.notify("Unable to start the Pixel Eraser.", "bad");
      setPixelEraserOn(false);
      return;
    }

    ctx.drawImage(source, 0, 0, sourceWidth, sourceHeight);

    const upperCanvas = canvas.upperCanvasEl;
    let drawing = false;
    let hasErased = false;
    let lastPoint: { x: number; y: number } | null = null;

    // Convert a canvas pointer position into the photo's pixel coordinates.
    const getImagePoint = (event: PointerEvent) => {
      const scenePoint = canvas.getScenePoint(event);

      const inverse = util.invertTransform(img.calcTransformMatrix());

      const local = util.transformPoint(scenePoint, inverse);

      const x = ((local.x + img.width / 2) / img.width) * sourceWidth;

      const y = ((local.y + img.height / 2) / img.height) * sourceHeight;

      return { x, y };
    };

    const insideImage = (point: { x: number; y: number }) =>
      point.x >= 0 &&
      point.y >= 0 &&
      point.x <= sourceWidth &&
      point.y <= sourceHeight;

    // Erase a round brush stroke from the photo's own pixels.
    const eraseTo = (point: { x: number; y: number }) => {
      if (!lastPoint || !ctx) return;

      ctx.save();
      ctx.globalCompositeOperation = "destination-out";
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.lineWidth = brushSize;

      ctx.beginPath();
      ctx.moveTo(lastPoint.x, lastPoint.y);
      ctx.lineTo(point.x, point.y);
      ctx.stroke();
      ctx.restore();

      lastPoint = point;
      hasErased = true;

      // Update the selected Fabric image, not the entire design.
      img.setElement(workCanvas);
      img.setCoords();

      canvas.requestRenderAll();
    };

    const onPointerDown = (event: PointerEvent) => {
      if (event.button !== 0) return;

      const point = getImagePoint(event);

      if (!point) return;

      eraseTo(point);

      // Prevent Fabric from moving the image while erasing.
      event.preventDefault();
      event.stopImmediatePropagation();

      drawing = true;
      lastPoint = point;

      upperCanvas.setPointerCapture(event.pointerId);

      // Erase a dot for a single click.
      ctx.save();
      ctx.globalCompositeOperation = "destination-out";
      ctx.beginPath();
      ctx.arc(point.x, point.y, brushSize / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      hasErased = true;
      img.setElement(workCanvas);
      img.setCoords();
      canvas.requestRenderAll();
    };

    const onPointerMove = (event: PointerEvent) => {
      if (!drawing) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      eraseTo(getImagePoint(event));
    };

    const finishStroke = (event: PointerEvent) => {
      if (!drawing) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      drawing = false;
      lastPoint = null;

      if (hasErased) {
        img.setCoords();
        canvas.requestRenderAll();
        ed.changed();
        ed.refresh();
      }
    };

    upperCanvas.addEventListener("pointerdown", onPointerDown, true);

    upperCanvas.addEventListener("pointermove", onPointerMove, true);

    upperCanvas.addEventListener("pointerup", finishStroke, true);

    upperCanvas.addEventListener("pointercancel", finishStroke, true);

    return () => {
      upperCanvas.removeEventListener("pointerdown", onPointerDown, true);

      upperCanvas.removeEventListener("pointermove", onPointerMove, true);

      upperCanvas.removeEventListener("pointerup", finishStroke, true);

      upperCanvas.removeEventListener("pointercancel", finishStroke, true);
    };
  }, [pixelEraserOn, img, brushSize, ed]);

  if (!img) {
    return (
      <div className={PANEL}>
        <Head title="Edit photo" onBack={() => ed.setPanel("photos")} />
        <p className={HINT_TOP}>Select a photo on the page to edit it.</p>
      </div>
    );
  }

  const cur: Look = look ?? readLook(img);
  const update = (patch: Partial<Look>) => {
    const next: Look = {
      ...cur,
      ...patch,
      adj: { ...cur.adj, ...(patch.adj || {}) },
    };
    setLook(next);
    applyLook(img, next);
    ed.canvas.requestRenderAll();
    ed.changed();
  };
  const shadow = img.shadow;

  return (
    <div className={PANEL}>
      <Head title="Edit photo" onBack={() => ed.setPanel("photos")} />
      <div className="mb-3 flex flex-wrap gap-[6px]">
        <button
          className={btn("soft", "sm")}
          disabled={!!ed.busy}
          onClick={() => ed.removeImageBackground(img)}
        >
          <Icon name="wand" size={16} />{" "}
          {ed.busy === "removebg" ? "Removing…" : "BG Remover"}
        </button>
        <button
          className={btn("soft", "sm")}
          disabled={!!ed.busy}
          onClick={() => ed.magicLayers(img)}
          title="Split the subject from its background into two layers"
        >
          <Icon name="layers" size={16} />{" "}
          {ed.busy === "magic" ? "Separating…" : "Magic layers"}
        </button>
        <button
          className={btn("plain", "sm")}
          disabled={!!ed.busy}
          onClick={() => ed.upscaleImage(img, 2)}
        >
          <Icon name="resize" size={16} />{" "}
          {ed.busy === "upscale" ? "Upscaling…" : "Upscale 2×"}
        </button>
        <button
          className={btn("plain", "sm")}
          disabled={!!ed.busy}
          onClick={() => ed.upscaleImage(img, 4)}
        >
          4×
        </button>
        <button
          className={btn("plain", "sm")}
          onClick={() => ed.startCrop(img)}
        >
          <Icon name="crop" size={16} /> Crop
        </button>
        <button
          type="button"
          className={btn(pixelEraserOn ? "accent" : "plain", "sm")}
          onClick={() => setPixelEraserOn((active) => !active)}
          disabled={!ed.canvas}
        >
          <Icon name="eraser" size={16} />
          {pixelEraserOn ? "Finish erasing" : "Pixel Eraser"}
        </button>
      </div>
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          ["filters", "Filters"],
          ["adjust", "Adjust"],
          ["effects", "Effects"],
        ]}
      />

      {tab === "filters" && (
        <>
          <div className="grid grid-cols-3 gap-2">
            {LOOKS.map((l: { key: string; label: string }) => (
              <button
                key={l.key}
                className="grid cursor-pointer gap-1 border-0 bg-transparent p-0 text-center text-xs text-st-ink"
                onClick={() =>
                  update({
                    preset: l.key,
                    intensity: cur.preset === l.key ? cur.intensity : 100,
                  })
                }
              >
                {previews[l.key] ? (
                  <img
                    className={`aspect-square w-full rounded-10 object-cover${cur.preset === l.key ? " ring-[3px] ring-st-green" : ""}`}
                    src={previews[l.key]}
                    alt=""
                  />
                ) : (
                  <span className={`${SKEL} aspect-square w-full`} />
                )}
                <span
                  className={
                    cur.preset === l.key ? "font-bold text-st-green-dark" : ""
                  }
                >
                  {l.label}
                </span>
              </button>
            ))}
          </div>
          {cur.preset !== "none" && (
            <Slider
              label="Intensity"
              value={cur.intensity}
              min={0}
              max={100}
              onChange={(v: number) => update({ intensity: v })}
            />
          )}
        </>
      )}

      {pixelEraserOn && (
        <div className="my-3">
          <label className="mb-1 block text-xs font-medium">
            Brush size: {brushSize}px
          </label>
          <input
            type="range"
            min={4}
            max={100}
            value={brushSize}
            onChange={(event) => setBrushSize(Number(event.target.value))}
            className="w-full"
          />
          <p className="mt-1 text-xs text-st-muted">
            Click and drag over the photo to erase pixels.
          </p>
        </div>
      )}

      {tab === "adjust" && (
        <>
          {ADJUSTMENTS.map(
            (a: { key: string; label: string; min: number; max: number }) => (
              <Slider
                key={a.key}
                label={a.label}
                value={cur.adj[a.key] || 0}
                min={a.min}
                max={a.max}
                onChange={(v: number) => update({ adj: { [a.key]: v } })}
              />
            ),
          )}
          <div className={FIELD_ROW}>
            <span>Tint</span>
            <ColorPicker
              value={cur.tint?.color || "#ff914d"}
              onChange={(v) => {
                if (v === null) return;
                update({ tint: { color: v, amount: cur.tint?.amount || 30 } });
              }}
              label="Tint colour"
              brandColors={ed.brand.colors || []}
            />
          </div>
          {cur.tint && (
            <Slider
              label="Tint strength"
              value={cur.tint.amount}
              min={0}
              max={100}
              onChange={(v: number) =>
                update({ tint: { ...cur.tint!, amount: v } })
              }
            />
          )}
          <button
            className={linkBlock}
            onClick={() =>
              update({
                adj: Object.fromEntries(
                  ADJUSTMENTS.map((a: { key: string }) => [a.key, 0]),
                ),
                tint: null,
              })
            }
          >
            Reset adjustments
          </button>

          <div className="mt-5 border-t border-st-line pt-4">
            <h3 className="mb-3 text-sm font-semibold">Photo Border</h3>

            <label className="mb-2 block text-xs font-medium">
              Thickness: {borderWidth}px
            </label>

            <input
              type="range"
              min={0}
              max={50}
              step={1}
              value={borderWidth}
              onChange={(event) => {
                const width = Number(event.target.value);
                setBorderWidth(width);

                img.set({
                  stroke: width > 0 ? borderColor : undefined,
                  strokeWidth: width,
                  strokeUniform: true,
                  strokeDashArray:
                    borderStyle === "dashed"
                      ? [10, 6]
                      : borderStyle === "dotted"
                        ? [2, 5]
                        : undefined,
                });

                img.setCoords();
                ed.canvas.requestRenderAll();
                ed.changed();
                ed.refresh();
              }}
              className="w-full"
            />

            <label className="mb-2 mt-4 block text-xs font-medium">
              Border color
            </label>

            <input
              type="color"
              value={borderColor}
              onChange={(event) => {
                const color = event.target.value;
                setBorderColor(color);

                img.set({
                  stroke: borderWidth > 0 ? color : undefined,
                });

                ed.canvas.requestRenderAll();
                ed.changed();
                ed.refresh();
              }}
              className="h-10 w-full cursor-pointer rounded-md"
            />

            <label className="mb-2 mt-4 block text-xs font-medium">
              Border style
            </label>

            <select
              value={borderStyle}
              onChange={(event) => {
                const style = event.target.value as
                  | "solid"
                  | "dashed"
                  | "dotted";

                setBorderStyle(style);

                img.set({
                  strokeDashArray:
                    style === "dashed"
                      ? [10, 6]
                      : style === "dotted"
                        ? [2, 5]
                        : undefined,
                });

                ed.canvas.requestRenderAll();
                ed.changed();
                ed.refresh();
              }}
              className="w-full rounded-md border border-st-line bg-transparent p-2 text-sm"
            >
              <option value="solid">Solid</option>
              <option value="dashed">Dashed</option>
              <option value="dotted">Dotted</option>
            </select>

            <button
              type="button"
              className={`${btn("plain", "sm")} mt-3`}
              onClick={() => {
                img.set({
                  stroke: undefined,
                  strokeWidth: 0,
                  strokeDashArray: undefined,
                });

                setBorderWidth(0);
                setBorderColor("#ffffff");
                setBorderStyle("solid");

                img.setCoords();
                ed.canvas.requestRenderAll();
                ed.changed();
                ed.refresh();
              }}
            >
              Remove border
            </button>
          </div>
        </>
      )}

      {tab === "effects" && (
        <>
          <label className={CHECK}>
            <input
              className={CHECK_INPUT}
              type="checkbox"
              checked={!!shadow}
              onChange={(e) => {
                const s = Math.max(img.getScaledWidth(), img.getScaledHeight());
                img.set({
                  shadow: e.target.checked
                    ? {
                        color: "rgba(0,0,0,0.35)",
                        blur: s / 18,
                        offsetX: 0,
                        offsetY: s / 40,
                      }
                    : null,
                });
                ed.canvas.requestRenderAll();
                ed.changed();
                ed.refresh();
              }}
            />{" "}
            Drop shadow
          </label>
          {shadow && (
            <>
              <Slider
                label="Blur"
                value={Math.round(shadow.blur)}
                min={0}
                max={200}
                onChange={(v: number) => {
                  img.shadow.blur = v;
                  img.dirty = true;
                  ed.canvas.requestRenderAll();
                  ed.changed();
                  ed.refresh();
                }}
              />
              <Slider
                label="Distance"
                value={Math.round(shadow.offsetY)}
                min={0}
                max={150}
                onChange={(v: number) => {
                  img.shadow.offsetY = v;
                  img.dirty = true;
                  ed.canvas.requestRenderAll();
                  ed.changed();
                  ed.refresh();
                }}
              />
            </>
          )}
          <button
            className={btn("plain", "sm", true)}
            disabled={img.isBackgroundImage}
            onClick={() => ed.imageToBackground(img)}
          >
            Set image as background
          </button>
          {img.credit && <p className={FINEPRINT}>{img.credit}</p>}
        </>
      )}
    </div>
  );
}

// ================================================================ text effects

const EFFECT_PREVIEW: Record<string, React.CSSProperties> = {
  none: {},
  shadow: { textShadow: "3px 3px 4px rgba(0,0,0,.45)" },
  lift: { textShadow: "0 6px 10px rgba(0,0,0,.35)" },
  hollow: { color: "transparent", WebkitTextStroke: "1.5px #10231e" },
  splice: {
    color: "transparent",
    WebkitTextStroke: "1.2px #10231e",
    textShadow: "3px 3px 0 #ffd166",
  },
  outline: {
    WebkitTextStroke: "5px #ffffff",
    paintOrder: "stroke fill",
    textShadow: "0 0 0 #fff",
    background: "#dfe7e3",
  },
  neon: { color: "#8ff5c9", textShadow: "0 0 6px #0b7a5a, 0 0 12px #0b7a5a" },
  glitch: { textShadow: "-3px 0 #00e5ff", WebkitTextStroke: "0.6px #ff2d6f" },
  background: { background: "#ffd166", padding: "0 4px" },
};

function EffectsPanel({ ed }: { ed: EditorApi }) {
  const texts: any[] =
    ed.kind === "text"
      ? [ed.selected]
      : ed.kind === "multi"
        ? ed.selected
            .getObjects()
            .filter((t: any) => (t.type || "").toLowerCase() === "textbox")
        : [];
  const first = texts[0];
  const current = first?.effect || { type: "none" };

  if (!first) {
    return (
      <div className={PANEL}>
        <Head title="Effects" onBack={() => ed.setPanel("text")} />
        <p className={HINT_TOP}>Select text on the page to add an effect.</p>
      </div>
    );
  }

  const apply = (effect: TextEffect) => {
    texts.forEach((t) => {
      applyTextEffect(t, effect);
      t.initDimensions?.();
    });
    ed.canvas.requestRenderAll();
    ed.changed();
    ed.refresh();
  };
  const withColor = [
    "shadow",
    "splice",
    "outline",
    "glitch",
    "background",
  ].includes(current.type);
  const withAmount = !["none", "background"].includes(current.type);

  return (
    <div className={PANEL}>
      <Head title="Effects" onBack={() => ed.setPanel("text")} />
      <h3 className={H3}>Style</h3>
      <div className="grid grid-cols-3 gap-2">
        {TEXT_EFFECTS.map(([k, label]) => (
          <button
            key={k}
            className={`grid cursor-pointer justify-items-center gap-1 rounded-10 border px-1 pb-2 pt-[10px] text-st-ink ${current.type === k ? "border-st-green bg-st-green-soft shadow-[inset_0_0_0_1px_#0b7a5a]" : "border-st-line bg-white"}`}
            onClick={() =>
              apply({
                type: k,
                amount: 50,
                color:
                  k === "outline"
                    ? "#ffffff"
                    : k === "splice" || k === "background"
                      ? "#ffd166"
                      : k === "glitch"
                        ? "#00e5ff"
                        : "#000000",
              })
            }
          >
            <span
              className="rounded font-['Poppins',sans-serif] text-[30px] font-extrabold leading-[1.05] text-st-ink"
              style={EFFECT_PREVIEW[k]}
            >
              Ag
            </span>
            <small className="text-xs text-st-muted">{label}</small>
          </button>
        ))}
      </div>
      {withAmount && (
        <Slider
          label="Intensity"
          value={current.amount ?? 50}
          min={0}
          max={100}
          onChange={(v: number) => apply({ ...current, amount: v })}
        />
      )}
      {withColor && (
        <div className={FIELD_ROW}>
          <span>Colour</span>
          <ColorPicker
            value={current.color || "#000000"}
            onChange={(v: string | null) =>
              apply({ ...current, color: v || "#000000" })
            }
            label="Effect colour"
            brandColors={ed.brand.colors || []}
          />
        </div>
      )}
      <p className={FINEPRINT}>Text colour: {textColor(first)}</p>
    </div>
  );
}

// ================================================================ position

const ALIGN: [AlignWhere, IconName, string][] = [
  ["left", "alignL", "Left"],
  ["center", "alignC", "Centre"],
  ["right", "alignR", "Right"],
  ["top", "alignTop", "Top"],
  ["middle", "alignMiddle", "Middle"],
  ["bottom", "alignBottom", "Bottom"],
];

function PositionPanel({ ed }: { ed: EditorApi }) {
  const o = ed.selected;
  if (!o) {
    return (
      <div className={PANEL}>
        <Head title="Position" />
        <p className={HINT_TOP}>Select something on the page to arrange it.</p>
      </div>
    );
  }

  const multi = ed.kind === "multi";
  const b = o.getBoundingRect();
  const set = (props: Record<string, unknown>) => {
    o.set(props);
    o.setCoords();
    ed.canvas.requestRenderAll();
    ed.changed();
    ed.refresh();
  };

  return (
    <div className={PANEL}>
      <Head title="Position" />
      <h3 className={H3}>Arrange</h3>
      <div className="grid grid-cols-2 gap-[6px]">
        <button
          className={btn("plain", "sm")}
          onClick={() => ed.order("forward")}
        >
          <Icon name="up" size={16} /> Forward
        </button>
        <button
          className={btn("plain", "sm")}
          onClick={() => ed.order("backward")}
        >
          <Icon name="down" size={16} /> Backward
        </button>
        <button
          className={btn("plain", "sm")}
          onClick={() => ed.order("front")}
        >
          <Icon name="top" size={16} /> To front
        </button>
        <button className={btn("plain", "sm")} onClick={() => ed.order("back")}>
          <Icon name="bottom" size={16} /> To back
        </button>
      </div>

      <h3 className={H3}>{multi ? "Align elements" : "Align to page"}</h3>
      <div className="grid grid-cols-3 gap-[6px]">
        {ALIGN.map(([k, icon, l]) => (
          <button
            key={k}
            className={btn("plain", "align")}
            onClick={() => ed.align(k)}
          >
            <Icon name={icon} size={16} /> {l}
          </button>
        ))}
      </div>

      {multi && (
        <>
          <h3 className={H3}>Spacing</h3>
          <div className="grid grid-cols-2 gap-[6px]">
            <button
              className={btn("plain", "sm")}
              disabled={o.getObjects().length < 3}
              onClick={() => ed.distribute("h")}
            >
              <Icon name="distH" size={16} /> Space across
            </button>
            <button
              className={btn("plain", "sm")}
              disabled={o.getObjects().length < 3}
              onClick={() => ed.distribute("v")}
            >
              <Icon name="distV" size={16} /> Space down
            </button>
          </div>
          <button
            className={`${btn("plain", "sm", true)} mt-3`}
            onClick={ed.group}
          >
            <Icon name="group" size={16} /> Group (Ctrl+G)
          </button>
        </>
      )}

      {!multi && (
        <>
          <h3 className={H3}>Advanced</h3>
          <div className="grid grid-cols-2 gap-2">
            <NumberBox
              label="X"
              value={Math.round(b.left)}
              onChange={(v: number) => set({ left: o.left + (v - b.left) })}
            />
            <NumberBox
              label="Y"
              value={Math.round(b.top)}
              onChange={(v: number) => set({ top: o.top + (v - b.top) })}
            />
            <NumberBox
              label="W"
              value={Math.round(o.getScaledWidth())}
              onChange={(v: number) => v > 0 && set({ scaleX: v / o.width })}
            />
            <NumberBox
              label="H"
              value={Math.round(o.getScaledHeight())}
              onChange={(v: number) => v > 0 && set({ scaleY: v / o.height })}
            />
            <NumberBox
              label="↻"
              value={Math.round(o.angle || 0)}
              suffix="°"
              onChange={(v: number) => {
                o.rotate(((v % 360) + 360) % 360);
                set({});
              }}
            />
          </div>
          {ed.kind === "group" && (
            <button
              className={`${btn("plain", "sm", true)} mt-3`}
              onClick={ed.ungroup}
            >
              <Icon name="ungroup" size={16} /> Ungroup (Ctrl+Shift+G)
            </button>
          )}
        </>
      )}

      <label className={`${CHECK} mt-3`}>
        <input
          className={CHECK_INPUT}
          type="checkbox"
          checked={ed.snapOn}
          onChange={(e) => ed.setSnapOn(e.target.checked)}
        />{" "}
        Snap to smart guides
      </label>
    </div>
  );
}
